import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { Bell, ChevronRight, UserPlus, Check, MessageSquare, Clock, Star, Calendar, UserCheck, X, Camera, Trash2 } from 'lucide-react';
import { useNotifications } from '../../hooks/useNotifications';
import NotificationToasts from './NotificationToasts';
import { Link } from 'react-router-dom';

const NotificationBell: React.FC = () => {
  const { items, unreadCount, loading, markRead, markAllRead, lastNew, clearLastNew } = useNotifications(20);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!open) return;
    const original = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = original; };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [open]);

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'connection_request':
        return <UserPlus size={18} className="text-secondary" />;
      case 'connection_accepted':
        return <Check size={18} className="text-accent2" />;
      case 'event_cancelled':
      case 'event_archived':
      case 'event_reminder':
        return <Clock size={18} className="text-secondary" />;
      case 'new_message':
        return <MessageSquare size={18} className="text-accent2" />;
      case 'application_update':
      case 'application_received':
        return <UserPlus size={18} className="text-secondary" />;
      case 'application_accepted':
      case 'accepted':
      case 'application_accepted_kanban':
        return <Check size={18} className="text-green-400" />;
      case 'application_rejected':
      case 'application_rejected_kanban':
        return <X size={18} className="text-red-400" />;
      case 'add_brief_nudge':
      case 'brief_posted':
        return <MessageSquare size={18} className="text-blue-400" />;
      case 'companion_request':
        return <UserCheck size={18} className="text-secondary" />;
      case 'favorite_added':
        return <Star size={18} className="text-secondary fill-secondary" />;
      case 'meeting_proposal_sent':
      case 'meeting_proposal_accepted':
      case 'meeting_proposal_rejected':
        return <Calendar size={18} className="text-secondary" />;
      case 'photo_reminder':
        return <Camera size={18} className="text-secondary" />;
      default:
        return <Bell size={18} className="text-contrast-500" />;
    }
  };

  const getNotificationLink = (notification: any) => {
    const eventId = notification.related_id || notification.metadata?.event_id;
    const threadId = notification.metadata?.thread_id;

    switch (notification.type) {
      case 'connection_request':
        return '/tribe?tab=requests';
      case 'connection_accepted':
        return '/tribe?tab=my';
      case 'new_message':
        return `/messages${notification.metadata?.sender_id ? `?userId=${notification.metadata.sender_id}` : ''}`;
      case 'application_accepted':
      case 'accepted':
        // Navigate to the group chat for this specific Vybe
        if (threadId) {
          return `/messages?thread=${threadId}`;
        } else if (eventId) {
          // Fallback: go to the event detail page where they can access chat
          return `/events/${eventId}`;
        }
        return '/messages';
      case 'application_received':
        // Navigate to the specific Vybe's applications section
        if (eventId) {
          return `/events/${eventId}#applications`;
        }
        return '/myvybes';
      case 'add_brief_nudge':
      case 'brief_posted':
        // Navigate to messages with thread context, or event page
        if (threadId) {
          return `/messages?thread=${threadId}`;
        } else if (eventId) {
          return `/events/${eventId}`;
        }
        return '/messages';
      case 'application_accepted_kanban':
      case 'application_rejected_kanban':
      case 'event_cancelled':
      case 'event_archived':
      case 'event_reminder':
        return eventId ? `/events/${eventId}` : '/myvybes';
      case 'verification_complete':
        return '/myAccount';
      case 'photo_reminder':
        return '/myAccount';
      default:
        return eventId ? `/events/${eventId}` : '#';
    }
  };

  const dropdownContent = open && (
    <>
      <div
        className="nav-popover-backdrop"
        onClick={() => setOpen(false)}
        aria-hidden="true"
      />

      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 20 }}
        transition={{ type: 'spring', damping: 25, stiffness: 300 }}
        className="nav-popover-panel rounded-2xl border border-theme-cards bg-theme-cards shadow-2xl flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-3 py-2">
          <div className="text-sm font-semibold text-white">Notifications</div>
          <div className="flex items-center gap-2">
            {unreadCount > 0 && (
              <button onClick={markAllRead} className="text-xs text-white hover:text-theme-text-secondary">
                Mark all as read
              </button>
            )}
            <button
              onClick={() => setOpen(false)}
              className="text-theme-text-secondary hover:text-white p-1"
              aria-label="Close"
            >
              <X size={18} />
            </button>
          </div>
        </div>

        <div className="max-h-[340px] overflow-auto">
          {loading && <div className="px-3 py-3 text-sm opacity-60">Loading…</div>}
          {!loading && items.length === 0 && (
            <div className="px-3 py-6 text-sm opacity-60 text-center">You're all caught up 🎉</div>
          )}
          {items.map((n) => {
            const link = getNotificationLink(n);
            const eventId = n.related_id || n.metadata?.event_id;
            const threadId = n.metadata?.thread_id;

            // Determine button text based on notification type
            let buttonText = '';
            if (['application_accepted', 'accepted', 'add_brief_nudge', 'brief_posted'].includes(n.type || '')) {
              buttonText = 'Go to Chat';
            } else if (n.type === 'application_received') {
              buttonText = 'Go to Vybe';
            } else if (eventId) {
              buttonText = 'View';
            }

            return (
              <div
                key={n.id}
                className="flex items-start gap-3 px-3 py-3 border-b border-theme-cards last:border-b-0 group"
              >
                <div className="mt-0.5 flex-shrink-0">{getNotificationIcon(n.type || '')}</div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-white leading-snug mb-1">{n.content || n.message || n.title || 'Notification'}</div>
                  <div className="flex items-center justify-between gap-2">
                    <div className="text-xs text-white opacity-60">
                      {new Date(n.created_at).toLocaleString()}
                    </div>
                    {buttonText && link !== '#' && (
                      <Link
                        to={link}
                        onClick={(e) => {
                          e.stopPropagation();
                          setOpen(false);
                        }}
                        className="text-xs font-medium px-3 py-1 rounded-full bg-gradient-to-r from-red-500 to-pink-600 text-white hover:from-red-600 hover:to-pink-700 transition-all flex-shrink-0"
                      >
                        {buttonText}
                      </Link>
                    )}
                  </div>
                </div>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    markRead(n.id);
                  }}
                  className="mt-0.5 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity text-white/60 hover:text-white p-1"
                  aria-label="Delete notification"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            );
          })}
        </div>

        <div className="flex items-center justify-between px-3 py-2 text-xs opacity-70">
          <span>Hover to delete notifications</span>
          <ChevronRight className="h-4 w-4" />
        </div>
      </motion.div>
    </>
  );

  return (
    <>
      {/* Bell button */}
      <button
        onClick={() => setOpen((o) => !o)}
        className={`relative flex items-center justify-center py-2 focus:outline-none focus:ring-0 ${
          unreadCount > 0 ? 'active' : ''
        }`}
        aria-label="Open notifications"
      >
        <Bell className="w-6 h-6" />
        {unreadCount > 0 && (
          <span className="absolute top-1 right-0 inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-red-500 px-1.5 text-[10px] font-bold text-white">
            {unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown - rendered via portal */}
      {createPortal(
        <AnimatePresence>
          {dropdownContent}
        </AnimatePresence>,
        document.body
      )}

      {/* Toast on new arrival */}
      <NotificationToasts item={lastNew} onClose={clearLastNew} />
    </>
  );
};

export default NotificationBell;
